import type { JSX } from 'react';
import { TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ClickableTableRow } from '@/shared/components/ClickableTableRow';
import type { JobPosition } from '../types/jobPosition.types';
import { JobPositionStatusBadge } from './JobPositionStatusBadge';
import { JobPositionActions } from './JobPositionActions';

export interface JobPositionRowProps {
  jobPosition: JobPosition;
  onEdit: (jobPosition: JobPosition) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string, archive: boolean) => void;
  onNavigate: (jobPosition: JobPosition) => void;
}

function TitleCell({
  title,
  department,
  onNavigate,
}: {
  title: string;
  department?: string | null;
  onNavigate: () => void;
}): React.JSX.Element {
  return (
    <TableCell>
      <div>
        <button
          type="button"
          onClick={onNavigate}
          className="font-medium text-left hover:underline hover:text-primary cursor-pointer"
        >
          {title}
        </button>
        {department && <p className="text-sm text-muted-foreground">{department}</p>}
      </div>
    </TableCell>
  );
}

function PositionFactsCells({ jobPosition }: { jobPosition: JobPosition }): JSX.Element {
  return (
    <>
      <TableCell>{jobPosition.location}</TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {jobPosition.employmentType.replace('_', ' ')}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">
          {jobPosition.remotePolicy.replace('_', ' ')}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {jobPosition.experienceLevels.map((level) => (
            <Badge key={level} variant="outline" className="text-xs">
              {level}
            </Badge>
          ))}
        </div>
      </TableCell>
    </>
  );
}

export function JobPositionRow({
  jobPosition,
  onEdit,
  onDelete,
  onArchive,
  onNavigate,
}: JobPositionRowProps): JSX.Element {
  const handleNavigate = (): void => {
    onNavigate(jobPosition);
  };

  return (
    <ClickableTableRow
      onActivate={handleNavigate}
      label={jobPosition.title}
      testId={`job-position-row-${jobPosition.id}`}
    >
      <TitleCell
        title={jobPosition.title}
        department={jobPosition.department}
        onNavigate={handleNavigate}
      />
      <PositionFactsCells jobPosition={jobPosition} />
      <TableCell>
        <JobPositionStatusBadge status={jobPosition.status} />
      </TableCell>
      <TableCell className="text-right">
        <JobPositionActions
          jobPosition={jobPosition}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
        />
      </TableCell>
    </ClickableTableRow>
  );
}
